import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import { adjustOpacity } from '../../../../theme/utils';
import {
  EXPERIMENT_MIN_SAMPLE_SIZE_INPUT,
  EXPERIMENT_MAX_SAMPLE_SIZE_INPUT,
} from '../../../../config/constants';

/**
 * ExperimentDetailsForm Component
 *
 * Form fields for experiment name, description, traffic allocation, minimum sample size, and statistical significance.
 *
 * @param {Object} props
 * @param {Object} props.formData - Form data object
 * @param {Object} props.errors - Form errors object
 * @param {Function} props.onInputChange - Handler for input changes
 * @param {boolean} props.isDescriptionExpanded - Whether description field is expanded
 * @param {Function} props.onToggleDescription - Handler to toggle description expansion
 * @param {Function} props.shouldShowMoreButton - Function to determine if show more button should be shown
 * @param {Array} props.trafficStrategies - Available traffic allocation strategies
 * @param {string} props.safeTrafficAllocation - Safe traffic allocation value
 */
function ExperimentDetailsForm({
  formData,
  errors,
  onInputChange,
  isDescriptionExpanded,
  onToggleDescription,
  shouldShowMoreButton,
  trafficStrategies = [],
  safeTrafficAllocation,
}) {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontSize: '1rem',
            fontWeight: 600,
            mb: 2,
            color: theme.palette.text.primary,
          }}
        >
          Experiment Details
        </Typography>
        <Grid container spacing={2}>
          {/* Left Column: Experiment Name and Description */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ pt: 1.5 }}>
              <TextField
                fullWidth
                label="Experiment Name"
                value={formData.name}
                onChange={e => onInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
                size="small"
              />
            </Box>
            <Box sx={{ pt: 1.5, position: 'relative' }}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={e => onInputChange('description', e.target.value)}
                multiline
                rows={isDescriptionExpanded ? 20 : 4}
                size="small"
                placeholder="Provide a detailed description of your experiment..."
                sx={{
                  '& .MuiInputBase-input': {
                    paddingLeft: '14px',
                    paddingRight: shouldShowMoreButton(formData.description)
                      ? '80px'
                      : '14px',
                    paddingBottom: shouldShowMoreButton(formData.description)
                      ? '32px'
                      : '14px',
                    overflow: 'hidden',
                    resize: 'none',
                  },
                  '& textarea': {
                    overflow: 'hidden !important',
                    resize: 'none',
                  },
                }}
              />
              {shouldShowMoreButton(formData.description) && (
                <Button
                  onClick={onToggleDescription}
                  sx={{
                    position: 'absolute',
                    right: '8px',
                    bottom: '8px',
                    minWidth: 'auto',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    color: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                    zIndex: 1,
                    '&:hover': {
                      backgroundColor:
                        theme.palette.primary.light ||
                        adjustOpacity(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  {isDescriptionExpanded ? 'Show less' : 'Show more'}
                </Button>
              )}
            </Box>
          </Grid>

          {/* Right Column: Traffic Allocation, Minimum Sample Size, and Statistical Significance */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ pt: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Traffic Allocation</InputLabel>
                <Select
                  value={safeTrafficAllocation}
                  onChange={e =>
                    onInputChange('traffic_allocation', e.target.value)
                  }
                  displayEmpty
                >
                  {trafficStrategies.length === 0 && (
                    <MenuItem value="" disabled>
                      Loading strategies...
                    </MenuItem>
                  )}
                  {trafficStrategies.map(strategy => (
                    <MenuItem key={strategy.name} value={strategy.name}>
                      {strategy.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ pt: 1.5 }}>
              <TextField
                fullWidth
                label="Minimum Sample Size"
                type="number"
                value={formData.minimum_sample_size}
                onChange={e =>
                  onInputChange('minimum_sample_size', parseInt(e.target.value))
                }
                inputProps={{
                  min: EXPERIMENT_MIN_SAMPLE_SIZE_INPUT,
                  max: EXPERIMENT_MAX_SAMPLE_SIZE_INPUT,
                }}
                size="small"
              />
            </Box>
            <Box sx={{ pt: 1.5 }}>
              <TextField
                fullWidth
                label="Statistical Significance"
                type="number"
                value={formData.statistical_significance}
                onChange={e =>
                  onInputChange(
                    'statistical_significance',
                    parseFloat(e.target.value)
                  )
                }
                inputProps={{ min: 0.8, max: 0.99, step: 0.01 }}
                size="small"
              />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default ExperimentDetailsForm;
